<template>
  <nuxt-link class="assessment-chapter" :to="path">
    <div class="details">
      <div class="title">
        <span class="prefix"> Chapitre {{ index + 1 }} : </span>
        <span class="name">
          {{ category.title }}
        </span>
      </div>
      <ProgressBarLabel
        class="progress-bar"
        theme="secondary"
        :show-label="false"
        :show-quit="false"
        :count="progressCount"
        :total="progressTotal"
      />
    </div>
    <div class="arrow">
      <icon class="graphic arrow-off" name="nextSmall" />
      <icon class="graphic arrow-on" name="nextSmall" />
    </div>
  </nuxt-link>
</template>
<script setup lang="ts">
import type { NuxtLinkProps } from '#app';

interface Props extends NuxtLinkProps {
  category: CategorySchema;
  libraryBookId: string;
  index: number;
  mProgressionByChapter: Map<
    string,
    { chapterId: string; total: number; count: number }
  >;
}

const props = defineProps({
  category: null,
  libraryBookId: null,
  index: null,
  mProgressionByChapter: null
});

const route = useRoute();

const uuid = computed(() => route.params.lb_or_eval as string);

const progressChapter = computed(() => {
  if (props.mProgressionByChapter.has(props.category.id)) {
    return props.mProgressionByChapter.get(props.category.id);
  }
});

const progressCount = computed(() => {
  return progressChapter?.value?.count ? progressChapter.value.count : 0;
});

const progressTotal = computed(() => {
  return progressChapter?.value?.total ? progressChapter.value.total : 1;
});

const path = computed(
  () => `/${Sections.ChildForYou}/${uuid.value}/${props.category.slug}`
);
</script>
<style lang="scss" scoped>
.assessment-chapter {
  display: grid;
  grid-template-columns: 1fr auto;
  padding: var(--gap_xl) var(--gap_l);
  transition: border-color 500ms $ease-in-quad;
  border-color: var(--color_secondary_lighter);
  background-color: var(--color_neutral_25);
  gap: var(--gap_m);
  @include stroke('m', 'm');
}

.details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  transition: color 300ms $ease-out-quad;
  color: var(--color_neutral_600);
  gap: var(--gap_m) var(--gap_m);
}

.title {
  @include font('n', 800);
  .prefix {
    position: relative;
    color: var(--color_secondary);
  }
}

.arrow {
  position: relative;
  align-self: center;
  width: 2.4rem;
  height: 2.4rem;
  color: var(--color_secondary);
  .graphic {
    position: absolute;
    top: 0;
    left: 0;
    transform: translateX(0) scale(1);
    transition: transform 400ms $ease-out-back 200ms,
      opacity 50ms $ease-in-quad 200ms;
    opacity: 1;
  }
  .arrow-on {
    transform: translateX(-1.4rem) scale(0.8);
    transition: transform 400ms $ease-in-quad, opacity 50ms $ease-in-quad 100ms;
    opacity: 0;
  }
}

a {
  &:hover,
  &:focus {
    transition: border-color 500ms $ease-out-quad;
    border-color: var(--color_secondary_light);
    .details {
      color: var(--color_neutral_900);
    }
    .arrow-on {
      transform: translateX(0) scale(1);
      transition: transform 350ms $ease-out-back 200ms,
        opacity 80ms $ease-in-circ 200ms;
      opacity: 1;
    }
    .arrow-off {
      transform: translateX(1.8rem) scale(0.6);
      transition: transform 250ms $ease-in-back,
        opacity 100ms $ease-out-quad 160ms;
      opacity: 0;
    }
    .logo {
      .image {
        transform: translate(-0.1rem, -0.1rem) scale(1.1);
        transition: transform 450ms $ease-out-back-custom,
          opacity 450ms $ease-out-quad 0ms;
      }
      .shadow {
        transform: translate(0.3rem, 0.3rem) scale(1.09);
        opacity: 0.1;
      }
    }
  }
}
</style>
